import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'

import Input from "../../shared/components/form/input"
import ListChecklistItemRow from './ListRow/ListChecklistItemRow'
import Modal from '../../shared/modal/Modal'
import NewChecklistItem from './NewChecklistItem'
import Button from '../../shared/components/Button/Button'
import useMyPermission from '../../hooks/useMyPermission'
import './ChecklistItem.css'

function ChecklistItem({ selectItem, handleOnSelectItem, selectedData, onFinishSelect }) {

    const [token] = useState(localStorage.getItem('token') || '')
    const [checklistItems, setChecklistItems] = useState([])
    const [checklistGroups, setChecklistGroups] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [search, setSearch] = useState("")
    const [load, setLoad] = useState(false)
    const [selected, setSelected] = useState(selectedData || [])
    const [myPermission, setMyPermission] = useState({ group: "" })

    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "checklistItem_View")
    const myPermissionCreate = checkPermission(myPermission, "checklistItem_Create")

    const toggleSelect = (item) => {
        const next = selected.find((i) => i._id === item._id)
            ? selected.filter((i) => i._id !== item._id)
            : [...selected, item]
        setSelected(next)
        handleOnSelectItem && handleOnSelectItem(next)
    }

    const getAll = () => {
        const headers = { Authorization: `Bearer ${JSON.parse(token)}` }
        Promise.all([
            api.get(`/checklistItem/getall`, { headers }),
            api.get(`/checklistGroup/getall`, { headers }),
        ]).then(([itemsRes, groupsRes]) => {
            setChecklistItems(itemsRes.data.checklistItems)
            setChecklistGroups(groupsRes.data.checklistGroups)
            setMyPermission(itemsRes.data.permission)
            setLoad(true)
        })
    }

    useEffect(() => { getAll() }, [token])

    async function handleReorderSection(reorderedSectionItems) {
        const orderById = new Map(reorderedSectionItems.map((item, index) => [item._id, index]))
        setChecklistItems(prev => prev.map((item) =>
            orderById.has(item._id) ? { ...item, order: orderById.get(item._id) } : item
        ))

        const items = reorderedSectionItems.map((item, index) => ({ _id: item._id, order: index }))
        await api.patch('/checklistItem/reorder', { items }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).catch(() => { getAll() })
    }

    const sections = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        const filteredItems = checklistItems.filter((item) => (item.name || '').toLowerCase().includes(searchLowerCase))

        const orderByGroupId = {}
        checklistGroups.forEach((g, index) => { orderByGroupId[g._id] = g.order ?? index })

        const groupsMap = new Map()
        filteredItems.forEach((item) => {
            const groupId = item.group?.value || 'sem-grupo'
            const groupLabel = item.group?.label || 'Sem grupo'
            if (!groupsMap.has(groupId)) {
                groupsMap.set(groupId, { groupId, groupLabel, items: [] })
            }
            groupsMap.get(groupId).items.push(item)
        })

        const list = Array.from(groupsMap.values())
        list.forEach((section) => section.items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)))
        list.sort((a, b) => (orderByGroupId[a.groupId] ?? 999) - (orderByGroupId[b.groupId] ?? 999))

        return list
    }, [checklistItems, checklistGroups, search])

    const totalCount = useMemo(() => sections.reduce((acc, s) => acc + s.items.length, 0), [sections])

    if (!load) {
        return <div className="pg-loading">Carregando itens de checklist...</div>
    }

    if (!selectItem && !myPermissionView) {
        return (
            <div className="pg-empty">
                <p className="pg-empty-title">Sem permissão para visualizar itens de checklist</p>
            </div>
        )
    }

    return (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Itens de Checklist</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>}
                            {selectItem && onFinishSelect && (
                                <>
                                    <Button variant="finish" onClick={onFinishSelect}>Concluir</Button>
                                    <span className="pg-count" style={{ marginLeft: '24px' }}>
                                        {selected.length} {selected.length === 1 ? 'item selecionado' : 'itens selecionados'}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar item..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {totalCount} {totalCount === 1 ? 'item' : 'itens'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {totalCount > 0 ? (
                    sections.map((section) => (
                        <div key={section.groupId} className="ci-group-section">
                            <div className="ci-group-header">{section.groupLabel}</div>
                            <ListChecklistItemRow
                                dataArray={section.items}
                                handleOnSuccess={getAll}
                                selectItem={selectItem}
                                selectedData={selected}
                                onToggleSelect={toggleSelect}
                                myPermission={myPermission}
                                onReorder={handleReorderSection}
                                dragEnabled={!search}
                            />
                        </div>
                    ))
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum item encontrado</p>
                        <p className="pg-empty-subtitle">
                            {checklistItems.length === 0
                                ? 'Cadastre o primeiro item para começar.'
                                : 'Tente ajustar a busca.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={60}
                content={
                    <NewChecklistItem
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

        </section>
    )
}

export default ChecklistItem
